/**
 * O card de um lead no quadro: o porte de `montarCardLead` (`admin.html:6598-6935`), sem o arraste,
 * que mora no `Kanban.tsx` porque é dele a coluna de destino.
 *
 * A ORDEM DOS BLOCOS é a do legado (`admin.html:6730-6760`): nome e dados, estágio, vínculo,
 * produtos, origem (marketing), indicado por, gift cards, cupom, observações, ações e a linha de
 * status. Cada bloco aparece ou some pelo MESMO recorte de papel do legado (`regras.ts`).
 *
 * O ESTADO LOCAL NASCE DO LEAD na montagem e morre na próxima `renderizar()`, porque a tela troca a
 * chave dos cards a cada redesenho. É o mesmo efeito do `innerHTML` do legado: edição não salva
 * some quando o quadro redesenha.
 */

// Estado do React.
import { useState } from 'react';
// As peças da plataforma: o produto não desenha botão, campo, nem lista de escolha.
import { Botao, Campo, CampoTexto, IconeLixeira, Selecao, SelecaoMultipla } from '@complexity/ui';
// As gravações que o card faz, cada uma com o `admin.html:linha` no módulo da área.
import { atualizarLead, criarIndicadorExterno, excluirCompromissosDoLead, excluirLead } from '@studiolu/dados/areas/vendas';
// A linha de status do legado (`p.lead-status-msg`), peça comum.
import { LinhaDeStatus } from '../../comum/status-temporario.js';
// Os rótulos do funil.
import { ORIGENS_LEAD, PRODUTOS_LEAD, STATUS_LEAD } from './constantes.js';
// A regra pura do card.
import {
  construirOpcoesIndicadoPor,
  giftCardVigente,
  metaDoCard,
  opcoesDeStatus,
  resolverIndicador,
  rotuloGiftCard,
  textoConfirmarExclusao,
  textoDoCupom,
  textoGiftCardsDoLead,
  valorIndicadoPorDoLead,
  type ColunasDoIndicador,
  type LeadDoQuadro,
} from './regras.js';
// O que a tela entrega ao card.
import type { ContextoDoCartao } from './quadro.js';
// O símbolo do WhatsApp, com o traçado do legado.
import { IconeWhatsapp } from './icone-whatsapp.js';

// As chaves dos produtos na ordem das caixas: é a ordem em que o legado grava (`admin.html:6819`).
const ORDEM_PRODUTOS = Object.keys(PRODUTOS_LEAD);

export function CartaoLead({ lead, contexto }: { readonly lead: LeadDoQuadro; readonly contexto: ContextoDoCartao }) {
  const { dados, memoria, papel, recorte, acoes } = contexto;
  const { somenteFinanceiro, somenteMarketing, somenteLeituraFunil, consulta, podeExcluir } = recorte;
  // Vinculada: o card mostra o aviso em vez do select, e o nome abre a ficha (`admin.html:6624`).
  const vinculado = !!lead.user_id_vinculado;
  // Só quem edita o funil de verdade abre a ficha pelo card (`admin.html:6782`).
  const clicavel = vinculado && !somenteLeituraFunil;
  // A linha de dados, montada na hora do desenho como o legado.
  const meta = metaDoCard(lead, memoria.clientes, memoria.externos);

  // O select de estágio começa no status atual.
  const [status, setStatus] = useState(lead.status);
  // O vínculo começa em "(vincular a uma cliente)".
  const [vinculo, setVinculo] = useState('');
  // As caixas de produto marcadas, a partir do que o lead tem.
  const [produtos, setProdutos] = useState<string[]>(() => [...lead.produtos]);
  // O "(N)" do resumo só muda ao salvar, como o `textContent` do legado (`admin.html:6860`).
  const [resumo, setResumo] = useState(lead.produtos.length);
  // A origem que o marketing edita.
  const [origem, setOrigem] = useState(lead.origem);
  // As opções do "Indicado por", fotografadas no desenho, como o `innerHTML` do legado.
  const [opcoesIndicador] = useState(() => construirOpcoesIndicadoPor(memoria.clientes, memoria.externos));
  // O valor inicial só vale se existir como opção; senão o `<select>` do legado cai na primeira,
  // "(selecione)", e é isso que ele grava ao salvar (`admin.html:6690`).
  const [indicadoPor, setIndicadoPor] = useState(() => {
    const inicial = valorIndicadoPorDoLead(lead);
    return opcoesIndicador.some((o) => o.valor === inicial) ? inicial : '';
  });
  // O nome digitado para "Nenhum dos acima".
  const [indicadoNovo, setIndicadoNovo] = useState('');
  // Os gift cards marcados para vincular agora.
  const [giftSel, setGiftSel] = useState<readonly string[]>([]);
  // As observações, vazias quando o lead não tem.
  const [observacoes, setObservacoes] = useState(lead.observacoes || '');
  // A linha de status do card: "Salvando...", "Salvo!" ou o erro.
  const [mensagem, setMensagem] = useState('');
  // O instante do desenho, que decide se um gift card já venceu.
  const [agora] = useState(() => new Date());

  // Gift cards deste lead (só leitura) e os ainda vigentes, para vincular mais (`admin.html:6700-6701`).
  const giftCardsDoLead = memoria.giftCards.filter((gc) => gc.lead_id_resgate === lead.id);
  const giftCardsDisponiveis = memoria.giftCards.filter((gc) => giftCardVigente(gc, agora));
  // O cupom que a cliente informou no portal dela (`admin.html:6717`).
  const resgatesDoLead = memoria.resgates.filter((r) => r.lead_id === lead.id);
  // "Indicado por" só para quem edita o lead e quando a origem é indicação (`admin.html:6688`).
  const temIndicador = !somenteLeituraFunil && lead.origem === 'indicacao';
  // O select de vínculo só existe sem vínculo (`admin.html:6641`).
  const temSeletorDeCliente = !somenteLeituraFunil && !vinculado;

  // As clientes para vincular, em ordem alfabética `pt-BR`, TODAS (o comentário do legado diz "só
  // as sem lead", e o código lista todas: linha 47 do inventário).
  const opcoesDeCliente = [...memoria.clientes]
    .sort((a, b) => (a.nome_completo || a.email || a.user_id).localeCompare(b.nome_completo || b.email || b.user_id, 'pt-BR'))
    .map((c) => ({ valor: c.user_id, rotulo: c.nome_completo || c.email || c.user_id }));

  // O "Salvar" do administrador (`admin.html:6816-6874`), na mesma ordem de passos.
  async function salvar() {
    setMensagem('Salvando...');
    // Com o select de vínculo, vale o escolhido (vazio vira nulo); sem ele, o vínculo que já existe.
    const novoUserId = temSeletorDeCliente ? vinculo || null : lead.user_id_vinculado;
    const clienteVinculado = novoUserId ? memoria.clientes.find((c) => c.user_id === novoUserId) : null;
    // Os produtos na ordem das caixas, e não na ordem em que foram marcados.
    const produtosSelecionados = ORDEM_PRODUTOS.filter((chave) => produtos.includes(chave));
    const atualizacao: Record<string, unknown> = {
      user_id_vinculado: novoUserId,
      observacoes,
      produtos: produtosSelecionados,
      // O e-mail da cliente vinculada entra junto, só quando há cliente (`admin.html:6831`).
      ...(clienteVinculado ? { email: clienteVinculado.email || null } : {}),
    };
    // O indicador, quando o bloco existe.
    let colunas: ColunasDoIndicador | null = null;
    if (temIndicador) {
      const resolucao = resolverIndicador(indicadoPor, indicadoNovo, memoria.externos);
      if (resolucao.tipo === 'cadastrar') {
        const criado = await criarIndicadorExterno(dados, resolucao.nome);
        if (!criado.ok) {
          setMensagem('Erro ao salvar indicador: ' + criado.erro.mensagem);
          return;
        }
        // A pessoa nova entra na memória, como o `push` do legado (`admin.html:7501`).
        memoria.externos.push(criado.dados);
        colunas = { indicado_por_user_id: null, indicado_por_externo_id: criado.dados.id };
      } else {
        colunas = resolucao.colunas;
      }
      Object.assign(atualizacao, colunas);
    }
    const gravado = await atualizarLead(dados, lead.id, atualizacao);
    if (!gravado.ok) {
      setMensagem('Erro ao salvar: ' + gravado.erro.mensagem);
      return;
    }
    // A memória muda no lugar, como no legado (o e-mail NÃO: o legado não o copia de volta).
    lead.user_id_vinculado = novoUserId;
    lead.observacoes = observacoes;
    lead.produtos = produtosSelecionados;
    let precisaRerenderizar = false;
    if (colunas) {
      lead.indicado_por_user_id = colunas.indicado_por_user_id;
      lead.indicado_por_externo_id = colunas.indicado_por_externo_id;
      await acoes.carregarRanking();
      precisaRerenderizar = true;
    }
    // Resgatar gift card mexe em outra tabela: o legado recarrega tudo (`admin.html:6855-6858`).
    let precisaRecarregarTudo = false;
    if (giftSel.length) {
      await acoes.resgatarGiftCards(lead.id, giftSel);
      precisaRecarregarTudo = true;
    }
    setResumo(produtosSelecionados.length);
    setMensagem('Salvo!');
    if (precisaRecarregarTudo) {
      await acoes.carregarLeads();
      return;
    }
    if (precisaRerenderizar && !(temSeletorDeCliente && novoUserId)) acoes.renderizar();
    if (temSeletorDeCliente && novoUserId) {
      await acoes.carregarClientes();
      acoes.renderizar();
    }
  }

  // O "Salvar" do marketing: só origem e observações (`admin.html:6800-6814`).
  async function salvarMarketing() {
    setMensagem('Salvando...');
    const gravado = await atualizarLead(dados, lead.id, { origem, observacoes });
    if (!gravado.ok) {
      setMensagem('Erro ao salvar: ' + gravado.erro.mensagem);
      return;
    }
    lead.origem = origem;
    lead.observacoes = observacoes;
    setMensagem('Salvo!');
  }

  // A lixeira (`admin.html:6876-6890`): compromissos antes do lead, e os erros em aviso.
  async function excluir() {
    if (!(await acoes.confirmar(textoConfirmarExclusao(lead.nome)))) return;
    const compromissos = await excluirCompromissosDoLead(dados, lead.id);
    if (!compromissos.ok) {
      await acoes.alertar('Erro ao excluir compromissos do lead: ' + compromissos.erro.mensagem);
      return;
    }
    const apagado = await excluirLead(dados, lead.id);
    if (!apagado.ok) {
      await acoes.alertar('Erro ao excluir: ' + apagado.erro.mensagem);
      return;
    }
    memoria.leads = memoria.leads.filter((l) => l.id !== lead.id);
    await acoes.carregarClientes();
    acoes.renderizar();
  }

  return (
    <div className="flex flex-col gap-cx-2">
      {/* Nome e linha de dados. Vinculada, este bloco (e só ele) abre a ficha (`admin.html:6782-6788`). */}
      <div
        data-info-lead
        className={clicavel ? 'group cursor-pointer' : undefined}
        onClick={
          clicavel
            ? (evento) => {
                // Não sobe até o card: o clique é só a navegação.
                evento.stopPropagation();
                void acoes.abrirCliente(lead);
              }
            : undefined
        }
      >
        <p className="text-cx-base font-semibold text-texto group-hover:underline">{lead.nome || '(sem nome)'}</p>
        <p className="text-cx-sm text-texto-suave">
          {meta.telefone ? (
            <>
              {meta.telefone}
              {/* O link do WhatsApp Web, em nova aba (`admin.html:7713`). Dentro do bloco clicável,
                  como no legado: o clique também abre a ficha, porque o legado não o isola. */}
              <a
                href={meta.whatsapp ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                title="Abrir no WhatsApp Web"
                aria-label="Abrir no WhatsApp Web"
                // O alvo de toque cresce para 44px por um pseudo-elemento, sem mexer na linha de texto.
                className="relative ml-cx-1 inline-flex align-middle text-texto-suave after:absolute after:left-1/2 after:top-1/2 after:min-h-toque after:min-w-toque after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']"
              >
                <IconeWhatsapp className="h-cx-4 w-cx-4" />
              </a>
              {meta.outros.length ? ` | ${meta.outros.join(' · ')}` : ''}
            </>
          ) : (
            meta.outros.join(' · ')
          )}
        </p>
      </div>

      {/* O estágio: texto fixo para marketing, select para os outros (`admin.html:6667-6670`). */}
      {somenteMarketing ? (
        <p className="text-cx-sm text-texto-suave">Estágio: {STATUS_LEAD[lead.status] || lead.status}</p>
      ) : (
        <Selecao
          rotulo="Estágio"
          opcoes={opcoesDeStatus(lead.status, papel)}
          value={status}
          disabled={consulta}
          onChange={(evento) => {
            setStatus(evento.target.value);
            // O terceiro caminho de mover, além dos dois arrastes (`admin.html:6778`).
            void acoes.mover(lead.id, evento.target.value);
          }}
        />
      )}

      {/* O vínculo com a cliente do portal (`admin.html:6641-6652`). */}
      {!somenteLeituraFunil &&
        (vinculado ? (
          <p className="text-cx-sm text-texto-suave">Vinculada a uma cliente do portal. Clique no card pra abrir o cadastro.</p>
        ) : (
          <Selecao
            rotulo="Vincular a uma cliente"
            vazio="(vincular a uma cliente)"
            opcoes={opcoesDeCliente}
            value={vinculo}
            disabled={consulta}
            onChange={(evento) => setVinculo(evento.target.value)}
          />
        ))}

      {/* Os produtos, num bloco recolhível com as oito caixas (`admin.html:6654-6665`). O Chassi não
          tem peça de caixa de marcar: a caixa é a nativa, pintada por token. */}
      {!somenteLeituraFunil && (
        <details className="rounded-cx-md">
          <summary className="flex min-h-toque cursor-pointer items-center text-cx-xs font-medium uppercase tracking-wide text-texto-suave">
            {`Produtos${resumo ? ` (${resumo})` : ''}`}
          </summary>
          <div className="flex flex-col py-cx-1">
            {Object.entries(PRODUTOS_LEAD).map(([valor, rotulo]) => (
              <label key={valor} className="flex min-h-toque cursor-pointer items-center gap-cx-2 text-cx-sm text-texto">
                <input
                  type="checkbox"
                  value={valor}
                  checked={produtos.includes(valor)}
                  disabled={consulta}
                  onChange={(evento) => {
                    const marcado = evento.target.checked;
                    setProdutos((atual) => (marcado ? [...atual, valor] : atual.filter((p) => p !== valor)));
                  }}
                  className="h-cx-4 w-cx-4 shrink-0 accent-marca"
                />
                {rotulo}
              </label>
            ))}
          </div>
        </details>
      )}

      {/* A origem, que só o marketing edita (`admin.html:6672-6679`), na ordem do mapa do legado. */}
      {somenteMarketing && (
        <Selecao
          rotulo="Origem"
          opcoes={Object.entries(ORIGENS_LEAD).map(([valor, rotulo]) => ({ valor, rotulo }))}
          value={origem}
          onChange={(evento) => setOrigem(evento.target.value)}
        />
      )}

      {/* Quem indicou, já preenchido, com o nome novo para "Nenhum dos acima" (`admin.html:6681-6696`). */}
      {temIndicador && (
        <>
          <Selecao
            rotulo="Indicado por"
            vazio="(selecione)"
            opcoes={[...opcoesIndicador, { valor: 'novo', rotulo: 'Nenhum dos acima' }]}
            value={indicadoPor}
            disabled={consulta}
            onChange={(evento) => setIndicadoPor(evento.target.value)}
          />
          {indicadoPor === 'novo' && (
            <Campo
              rotulo="Nome de quem indicou"
              autoComplete="name"
              placeholder="Digite o nome..."
              value={indicadoNovo}
              disabled={consulta}
              onChange={(evento) => setIndicadoNovo(evento.target.value)}
            />
          )}
        </>
      )}

      {/* Gift cards: os já resgatados em texto e os vigentes para vincular (`admin.html:6698-6712`). */}
      {!somenteLeituraFunil &&
        (giftCardsDisponiveis.length ? (
          <SelecaoMultipla
            rotulo="Gift Card(s)"
            opcoes={giftCardsDisponiveis.map((gc) => ({ valor: String(gc.id), rotulo: rotuloGiftCard(gc) }))}
            valores={giftSel}
            aoMudar={setGiftSel}
            disabled={consulta}
            ajuda={giftCardsDoLead.length ? textoGiftCardsDoLead(giftCardsDoLead) : undefined}
          />
        ) : (
          <div className="flex flex-col gap-cx-1">
            <p className="text-cx-sm font-medium text-texto-suave">Gift Card(s)</p>
            {giftCardsDoLead.length ? (
              <p className="text-cx-sm text-texto-suave">{textoGiftCardsDoLead(giftCardsDoLead)}</p>
            ) : (
              <p className="text-cx-xs text-texto-fraco">Nenhum gift card disponível.</p>
            )}
          </div>
        ))}

      {/* O cupom informado pela cliente, só leitura (`admin.html:6714-6728`). */}
      {!somenteLeituraFunil && resgatesDoLead.length > 0 && (
        <div className="flex flex-col gap-cx-1">
          <p className="text-cx-sm font-medium text-texto-suave">Cupom de Desconto</p>
          {resgatesDoLead.map((resgate, indice) => (
            <p key={indice} className="text-cx-sm text-texto-suave">
              {textoDoCupom(resgate, memoria.cupons)}
            </p>
          ))}
        </div>
      )}

      {/* As observações; o financeiro não as vê (`admin.html:6742`). */}
      {!somenteFinanceiro && (
        <CampoTexto
          rotulo="Observações"
          linhas={2}
          value={observacoes}
          disabled={consulta}
          onChange={(evento) => setObservacoes(evento.target.value)}
        />
      )}

      {/* O salvar do marketing (`admin.html:6743-6746`). */}
      {somenteMarketing && (
        <div className="flex items-center justify-between gap-cx-2">
          <Botao variante="secundario" tamanho="sm" onClick={() => void salvarMarketing()}>
            Salvar
          </Botao>
        </div>
      )}

      {/* Salvar e lixeira: consulta não vê botão de ação, e excluir é só do administrador. */}
      {!somenteFinanceiro && !somenteMarketing && !consulta && (
        <div className="flex items-center justify-between gap-cx-2">
          <Botao variante="secundario" tamanho="sm" onClick={() => void salvar()}>
            Salvar
          </Botao>
          {podeExcluir && (
            /*
             * VERMELHA SEMPRE, por decisão do fundador (19/09/2026): toda ação de apagar é vermelha
             * em todo lugar. No legado ela é cinza e só avermelha no hover (`admin.html:940-945`,
             * `color:var(--muted)` e `:hover{ color:var(--erro) }`); a decisão fixa o vermelho do
             * hover como a cor de repouso. O `!` é porque o Chassi não tem variante de perigo e a cor
             * da variante fantasma venceria pela ordem do CSS (achado 2 da A2, em `clientes/index.tsx`).
             */
            <Botao
              variante="fantasma"
              tamanho="icone"
              className="!text-erro"
              title="Excluir lead"
              aria-label="Excluir lead"
              onClick={() => void excluir()}
            >
              <IconeLixeira aria-hidden="true" className="h-cx-4 w-cx-4" />
            </Botao>
          )}
        </div>
      )}

      <LinhaDeStatus texto={mensagem} />
    </div>
  );
}

